// src/components/sla/ai/SlaAiOverviewStrip.js
// KPI tiles for the SLA AI: live on-track / at-risk / breached, what the AI did in 7 days,
// 30-day compliance + average grade, pending approvals, engine (ML vs Node mirror) and mode.
import React, { memo } from 'react';
import {
    Badge, Box, Button, Flex, HStack, Icon, SimpleGrid, Skeleton, Text, Tooltip, useColorModeValue,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdCheckCircle, MdHourglassTop, MdPlayArrow, MdSwapHoriz, MdTimer, MdTrendingUp, MdWarningAmber } from 'react-icons/md';
import { fmtRelative, pct } from './slaAiFormat';

const Tile = memo(function Tile({ label, value, sub, icon, scheme = 'gray', loading, onClick, tip }) {
    const bg = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const iconBg = useColorModeValue(`${scheme}.50`, `${scheme}.900`);
    const body = (
        <Box
            bg={bg} borderWidth="1px" borderColor={border} borderRadius="xl" p={4} h="100%"
            as={onClick ? 'button' : 'div'} type={onClick ? 'button' : undefined} onClick={onClick} textAlign="left" w="100%"
            _hover={onClick ? { borderColor: `${scheme}.300` } : undefined}
        >
            <Flex justify="space-between" align="flex-start">
                <Box>
                    <Text fontSize="xs" fontWeight="700" color={muted} textTransform="uppercase" letterSpacing="wide">{label}</Text>
                    {loading ? <Skeleton h="28px" w="56px" mt={1} /> : <Text fontSize="2xl" fontWeight="800" lineHeight="1.2">{value}</Text>}
                    {sub && <Text fontSize="xs" color={muted} mt={0.5}>{sub}</Text>}
                </Box>
                <Flex bg={iconBg} borderRadius="lg" p={2}><Icon as={icon} color={`${scheme}.500`} boxSize={5} /></Flex>
            </Flex>
        </Box>
    );
    return tip ? <Tooltip label={tip} hasArrow openDelay={300}>{body}</Tooltip> : body;
});

function SlaAiOverviewStrip({ overview, loading, onRunNow, running, onShowPending, canRun = true }) {
    const o = overview || {};
    const live = o.live || {};
    const week = o.last_7_days || {};
    const month = o.last_30_days || {};
    const engine = o.engine || {};
    const muted = useColorModeValue('gray.500', 'gray.400');
    return (
        <Box>
            <Flex justify="space-between" align="center" mb={3} gap={2} flexWrap="wrap">
                <HStack spacing={2} flexWrap="wrap">
                    <Badge colorScheme={o.mode === 'auto' ? 'purple' : o.mode === 'recommend' ? 'blue' : 'gray'} px={2} py={0.5} borderRadius="full">
                        {o.mode === 'auto' ? 'Auto-reassign ON' : o.mode === 'recommend' ? 'Recommend only' : o.mode === 'off' ? 'SLA AI off' : '…'}
                    </Badge>
                    <Tooltip label={engine.ml_cooling_down ? `ML unreachable (${engine.lastError || 'error'}) — using the identical Node mirror until ${engine.ml_retry_at ? new Date(engine.ml_retry_at).toLocaleTimeString() : 'retry'}` : 'Python ML service answering'} hasArrow>
                        <Badge colorScheme={engine.ml_cooling_down || !engine.ml_enabled ? 'orange' : 'green'} variant="subtle" px={2} py={0.5} borderRadius="full">
                            <HStack spacing={1}><Icon as={MdAutoAwesome} /><Text>{engine.model_version || 'sla-grade'} · {engine.ml_cooling_down || !engine.ml_enabled ? 'Node mirror' : 'ML'}</Text></HStack>
                        </Badge>
                    </Tooltip>
                    {o.last_run && <Text fontSize="xs" color={muted}>Last sweep {fmtRelative(o.last_run.at)} · {o.last_run.scanned ?? 0} jobs checked</Text>}
                </HStack>
                {canRun && onRunNow && (
                    <Button size="sm" leftIcon={<MdPlayArrow />} colorScheme="purple" variant="outline" onClick={onRunNow} isLoading={running} loadingText="Running">
                        Run sweep now
                    </Button>
                )}
            </Flex>
            <SimpleGrid columns={{ base: 2, md: 4, xl: 7 }} spacing={3}>
                <Tile label="On track" value={live.on_track ?? '—'} sub={`${live.active ?? 0} open with SLA`} icon={MdCheckCircle} scheme="green" loading={loading} />
                <Tile label="At risk" value={live.at_risk ?? '—'} sub={`≥ ${o.thresholds?.warn_pct ?? 80}% of window used`} icon={MdWarningAmber} scheme="orange" loading={loading} />
                <Tile label="Breached" value={live.breached ?? '—'} sub="deadline passed" icon={MdTimer} scheme="red" loading={loading} />
                <Tile label="AI reassigned" value={week.reassigned ?? '—'} sub={`7 days · ${week.reverted ?? 0} reverted`} icon={MdSwapHoriz} scheme="purple" loading={loading} />
                <Tile label="Escalations" value={(week.escalations ?? 0) + (week.recommended ?? 0)} sub={`7 days · ${week.warnings ?? 0} warnings`} icon={MdTrendingUp} scheme="red" loading={loading} />
                <Tile label="Needs approval" value={o.pending_approval ?? '—'} sub="AI recommendations" icon={MdHourglassTop} scheme="blue" loading={loading} onClick={onShowPending} tip="Open the recommendations waiting for a supervisor" />
                <Tile label="Compliance" value={pct(month.compliance_rate)} sub={`30 days · avg score ${month.avg_score ?? '—'} · ${month.graded ?? 0} graded`} icon={MdAutoAwesome} scheme="teal" loading={loading} />
            </SimpleGrid>
        </Box>
    );
}

export default memo(SlaAiOverviewStrip);
